import { Alert, AlertTitle, Typography } from '@mui/material';
import React from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import awsRum from '../../../../../../../../../../awsRum';

function getResetKey(props: { session?: { id: string }, displays?: { id: string }[] }) {
  if (props.session) return props.session.id;
  if (props.displays) return props.displays.map((display) => display.id).join(':');
  return undefined;
}

function SectionErrorFallback(props: FallbackProps & { section: string }) {
  const { error, section } = props;
  const { t } = useTranslation();

  return (
    <Alert severity="error">
      <AlertTitle>{t('error.section-render.title', { section })}</AlertTitle>
      <Typography>
        {t('error.section-render.text')}
      </Typography>
      <Typography variant="caption">
        {t('error.section-render.details', { message: error.message })}
      </Typography>
    </Alert>
  );
}

export default function withSessionDetailsSectionErrorBoundary<P extends { session?: { id: string }, displays?: { id: string }[] }>(
  Component: React.ComponentType<P>,
  section: string,
) {
  function ComponentWithSessionDetailsSectionErrorBoundary(props: P) {
    return (
      <ErrorBoundary
        // eslint-disable-next-line react/no-unstable-nested-components
        FallbackComponent={(fallbackProps) => <SectionErrorFallback {...fallbackProps} section={section} />}
        onError={(error: Error) => {
          awsRum?.recordError(error);
          console.error(`Error rendering session details ${section}`, error);
        }}
        resetKeys={[getResetKey(props)]}
      >
        <Component {...props} />
      </ErrorBoundary>
    );
  }

  ComponentWithSessionDetailsSectionErrorBoundary.displayName = `withSessionDetailsSectionErrorBoundary(${Component.displayName || Component.name || 'Component'})`;

  return ComponentWithSessionDetailsSectionErrorBoundary;
}
